<h1 mat-dialog-title>Referral from another practice</h1>

<div mat-dialog-content class="rnr">
    <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>

    <div class="rnr__alert" *ngIf="loadError" data-test="load-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ loadError }}</div>
    </div>

    <ng-container *ngIf="referral">
        <div class="rnr__author" data-test="sender">
            <mat-icon>domain</mat-icon>
            <div>
                <div class="rnr__author-name">{{ referral.senderPracticeName }}</div>
                <div class="hint">Referred by {{ referral.referredByName }} · {{ referral.referredAt | date: 'd MMM y, HH:mm' }} · {{ urgencyLabels[referral.urgency] }}</div>
            </div>
        </div>

        <div class="rnr__note" data-test="reason">
            <h3>Reason for referral</h3>
            <p>{{ referral.reason }}</p>
        </div>

        <div class="rnr__note" *ngIf="referral.summaryText" data-test="summary">
            <h3>Summary of the patient file</h3>
            <p>{{ referral.summaryText }}</p>
        </div>

        <div class="rnr__note" data-test="patient">
            <h3>The patient, as sent</h3>
            <div *ngFor="let field of shownFields">
                <span *ngIf="fieldValue(field.key) as value"><span class="hint">{{ field.label }}:</span> {{ value }}</span>
            </div>
            <div class="rnr__warning" *ngIf="referral.patient['isDeceased'] === true">
                <mat-icon>warning_amber</mat-icon>
                <div>The referring practice records this patient as deceased<span *ngIf="fieldValue('dateOfDeath') as d"> ({{ d }})</span>.</div>
            </div>
        </div>

        <div class="rnr__allergies" *ngIf="referral.diagnoses?.allergies as allergies" [class.rnr__allergies--has]="allergies.items.length" data-test="allergies">
            <mat-icon>{{ allergies.items.length ? 'warning' : 'health_and_safety' }}</mat-icon>
            <span class="rnr__allergies-label">Allergies</span>
            <span class="rnr__allergy" *ngFor="let a of allergies.items" [matTooltip]="a.reaction ? 'Reaction: ' + a.reaction : ''">
                {{ a.allergy }}<span class="rnr__allergy-reaction" *ngIf="a.reaction"> · {{ a.reaction }}</span>
            </span>
            <span class="hint" *ngIf="!allergies.items.length">{{ allergies.noAllergies ? 'No known allergies' : 'None recorded by the referring practice' }}</span>
        </div>

        <div class="rnr__note" *ngIf="referral.diagnoses?.medicalHistory || referral.diagnoses?.medications" data-test="history">
            <h3>Medical history &amp; medication at {{ referral.senderPracticeName }}</h3>
            <div class="rnr__chips" *ngIf="referral.diagnoses?.medicalHistory?.length; else noHistory">
                <span class="rnr__chip rnr__chip--history" *ngFor="let h of referral.diagnoses!.medicalHistory!" [matTooltip]="h.year ? 'Since ' + h.year : ''">{{ h.surgery }}</span>
            </div>
            <ng-template #noHistory><p class="hint">No medical history recorded.</p></ng-template>
            <ng-container *ngIf="referral.diagnoses?.medications as meds">
                <div class="hint">Patient reported</div>
                <div class="rnr__chips" *ngIf="meds.reported.length; else noneReported">
                    <span class="rnr__chip rnr__chip--medication" *ngFor="let m of meds.reported" [matTooltip]="m.dosage || ''">{{ m.medication }}</span>
                </div>
                <ng-template #noneReported><p class="hint">None reported.</p></ng-template>
                <div class="hint">Active medication</div>
                <div class="rnr__chips" *ngIf="meds.active.length; else noneActive">
                    <span class="rnr__chip rnr__chip--medication" *ngFor="let m of distinctMedications(meds.active)" [matTooltip]="[m.dose, m.unit, m.frequency].join(' ').trim()">{{ m.name }}</span>
                </div>
                <ng-template #noneActive><p class="hint">None active.</p></ng-template>
            </ng-container>
        </div>

        <div class="rnr__note" *ngIf="referral.diagnoses as dx" data-test="diagnoses">
            <h3>Diagnoses at {{ referral.senderPracticeName }}</h3>
            <div class="rnr__working" *ngIf="workingDiagnosisLines(dx.workingDiagnosis) as lines">
                <div *ngFor="let line of lines; let first = first" [class.rnr__working-primary]="first">{{ line }}</div>
            </div>
            <div *ngIf="dx.codes.length; else noDiagnoses">
                <div *ngFor="let code of dx.codes" class="rnr__diagnosis">
                    <b>{{ code.code }}</b> {{ code.description || 'No description given' }}
                    <span class="hint" *ngIf="diagnosisMeta(code) as meta"> · {{ meta }}</span>
                </div>
                <p class="hint">Their active ICD-10 codes, as shared {{ dx.updatedAt | date: 'd MMM y' }}. Shown for reference: nothing is added to your coding.</p>
            </div>
            <ng-template #noDiagnoses><p class="hint">They record no active ICD-10 codes for this patient.</p></ng-template>
        </div>

        <div class="rnr__status" *ngIf="referral.status !== 'pending'" data-test="already-decided">
            <mat-icon>{{ referral.status === 'accepted' ? 'check_circle' : 'block' }}</mat-icon>
            <div>
                {{ referral.status === 'accepted' ? 'Accepted' : 'Declined' }}<span *ngIf="referral.decidedAt"> on {{ referral.decidedAt | date: 'd MMM y' }}</span>
                <div *ngIf="referral.declineReason" class="hint">Reason: {{ referral.declineReason }}</div>
            </div>
        </div>

        <p class="hint" *ngIf="referral.status === 'pending' && !referral.stillShared" data-test="not-shared">
            The referring practice has stopped sharing on this referral. You can still accept or decline it.
        </p>

        <ng-container *ngIf="referral.status === 'pending' && !declining">
            <h3>1. Which of your patients is this?</h3>
            <p class="hint" *ngIf="ranking">Looking for matching patients…</p>
            <mat-radio-group [(ngModel)]="form.patient" class="rnr__choices" data-test="patient-choice" *ngIf="!ranking">
                <ng-container *ngFor="let group of [exactCandidates, fuzzyCandidates]; let exact = first">
                    <div class="rnr__candidate" *ngFor="let candidate of group" data-test="candidate">
                        <mat-radio-button [value]="candidate.patient.id">
                            <b>{{ patientLabel(candidate.patient) }}</b>
                            <span class="hint"> · {{ exact ? 'Exact identity match' : candidate.score + '% · ' + candidate.band }}</span>
                        </mat-radio-button>
                        <div class="hint" *ngFor="let detail of candidate.details">{{ detail.label }}: {{ detail.resultValue }} · {{ detail.patientValue }}</div>
                        <div class="rnr__conflict" *ngFor="let conflict of candidate.conflicts"><mat-icon>warning_amber</mat-icon> {{ conflict }}</div>
                    </div>
                </ng-container>
                <p class="hint" *ngIf="!exactCandidates.length && !fuzzyCandidates.length" data-test="no-candidates">
                    None of your patients matched the details sent. Search all patients, or create the patient.</p>
                <mat-radio-button *ngIf="searchedPatient" [value]="searchedPatient.id" data-test="searched-patient">{{ searchedPatient.label }}</mat-radio-button>
                <mat-radio-button value="new" data-test="create-patient">A new patient: create {{ patientName }} from the details sent</mat-radio-button>
            </mat-radio-group>
            <button mat-stroked-button type="button" (click)="searchAllPatients()" [disabled]="ranking" data-test="search-all">Search all patients</button>
            <p class="hint" *ngIf="referral.shareMode === 'summary' && referral.summaryText">Accepting files the summary above on the patient as a read-only note.</p>

            <h3>2. How do you want to share back with {{ referral.senderPracticeName }}?</h3>
            <mat-radio-group [(ngModel)]="form.shareBack" class="rnr__choices" data-test="share-back">
                <mat-radio-button value="full" [disabled]="!referral.linkActive">This patient's timeline from your practice</mat-radio-button>
                <mat-radio-button value="summary" [disabled]="!referral.linkActive">A summary (you write it next)</mat-radio-button>
                <mat-radio-button value="none">Nothing for now (you can refer back later from the patient file)</mat-radio-button>
            </mat-radio-group>
            <p class="hint" *ngIf="form.shareBack === 'full'">Your practice's own entries are shared: notes, admissions, returned documents and
                ward moves. Anything filed from {{ referral.senderPracticeName }} is never sent back.</p>

            <ng-container *ngIf="form.shareBack === 'full'">
                <p class="rnr__label">Share later entries too?</p>
                <mat-radio-group [(ngModel)]="form.futureMode" class="rnr__choices" data-test="future-mode">
                    <mat-radio-button *ngFor="let mode of futureModes" [value]="mode">{{ futureModeLabels[mode] }}</mat-radio-button>
                </mat-radio-group>
                <mat-form-field appearance="outline" *ngIf="form.futureMode === 'until'">
                    <mat-label>Share until</mat-label>
                    <input matInput [matDatepicker]="untilPicker" [(ngModel)]="form.futureUntil" [min]="minFutureDate">
                    <mat-datepicker-toggle matSuffix [for]="untilPicker"></mat-datepicker-toggle>
                    <mat-datepicker #untilPicker></mat-datepicker>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Note to the practice (optional)</mat-label>
                    <textarea matInput [(ngModel)]="form.reason" maxlength="5000" rows="2" cdkTextareaAutosize data-test="share-back-reason"></textarea>
                </mat-form-field>
                <mat-checkbox [(ngModel)]="form.acknowledged" *ngIf="acknowledgement" data-test="acknowledge">{{ acknowledgement.text }}</mat-checkbox>
            </ng-container>

            <ul class="hint" *ngIf="submitAttempted && problems.length" data-test="problems">
                <li *ngFor="let problem of problems">{{ problem }}</li>
            </ul>
        </ng-container>

        <mat-form-field appearance="outline" class="full-width" *ngIf="declining">
            <mat-label>Why are you declining? The referring practice sees this.</mat-label>
            <textarea matInput [(ngModel)]="declineReason" [maxlength]="declineMax" rows="3" cdkTextareaAutosize data-test="decline-reason"></textarea>
        </mat-form-field>

        <div class="rnr__alert" *ngIf="actionError" data-test="action-error">
            <mat-icon>error_outline</mat-icon>
            <div>{{ actionError }}</div>
        </div>
    </ng-container>
</div>

<div mat-dialog-actions align="end">
    <button mat-button (click)="close()" [disabled]="isSaving">Close</button>
    <!-- M6: once accepted, the referral chat with the referring practice. -->
    <button mat-stroked-button *ngIf="referral?.status === 'accepted' && chatEnabled" (click)="openChat()" [disabled]="isSaving" data-test="referral-chat">
        <mat-icon>forum</mat-icon> Chat with {{ referral?.senderPracticeName || 'the practice' }}
    </button>
    <ng-container *ngIf="referral?.status === 'pending'">
        <ng-container *ngIf="!declining">
            <button mat-stroked-button color="warn" (click)="declining = true" [disabled]="isSaving" data-test="decline">Decline…</button>
            <button mat-flat-button color="primary" (click)="accept()" [disabled]="isSaving" data-test="accept">{{ isSaving ? 'Saving…' : 'Accept referral' }}</button>
        </ng-container>
        <ng-container *ngIf="declining">
            <button mat-button (click)="declining = false; actionError = null" [disabled]="isSaving">Back</button>
            <button mat-flat-button color="warn" (click)="decline()" [disabled]="isSaving || !declineReason.trim()" data-test="confirm-decline">
                {{ isSaving ? 'Saving…' : 'Decline referral' }}
            </button>
        </ng-container>
    </ng-container>
</div>
